/**
 * FEEDBACK WIDGET — APERCU DES DONNEES DEBUG (TICKET-CRM-14)
 * Transparence : montre à l'utilisateur ce qui sera joint au ticket.
 */

import { useState } from 'react';
import { ChevronDown, Database } from 'lucide-react';
import type { DebugSnapshot } from '../../lib/feedback/feedback.types';
import { cn } from '../../lib/utils';

interface DebugDataPreviewProps {
  snapshot: DebugSnapshot | null;
}

export function DebugDataPreview({ snapshot }: DebugDataPreviewProps) {
  const [open, setOpen] = useState(false);

  if (!snapshot) return null;

  const errorCount = snapshot.console.filter((e) => e.type === 'error').length;
  const actionCount = snapshot.userActions?.length ?? 0;
  const requestCount = snapshot.networkRequests?.length ?? 0;
  const failedRequestCount =
    snapshot.networkRequests?.filter((r) => r.status >= 400 || r.error).length ?? 0;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-md bg-zinc-100 p-3 text-sm transition-colors hover:bg-zinc-200"
      >
        <span className="flex items-center gap-2 font-medium text-zinc-700">
          <Database className="h-4 w-4" />
          Données de debug incluses
        </span>
        <span className="flex items-center gap-2 text-xs text-zinc-500">
          {errorCount > 0 && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700">
              {errorCount} erreur{errorCount > 1 ? 's' : ''}
            </span>
          )}
          {failedRequestCount > 0 && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700">
              {failedRequestCount} req KO
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>

      {open && (
        <div className="mt-2 space-y-2 rounded-md border border-zinc-200 p-3 text-xs text-zinc-600">
          <p>
            Page : <code className="rounded bg-zinc-100 px-1">{snapshot.navigation.pathname}</code>
          </p>
          <p>
            {snapshot.console.length} logs console · {actionCount} actions récentes · {requestCount}{' '}
            requêtes réseau
          </p>
          <p>
            Viewport {snapshot.metadata.viewportWidth}×{snapshot.metadata.viewportHeight} ·{' '}
            {snapshot.metadata.platform}
          </p>
          <p className="text-zinc-400">
            Les adresses email et paramètres de requête sont automatiquement masqués. Ces
            informations aident à reproduire et corriger le problème.
          </p>
        </div>
      )}
    </div>
  );
}
